/**
 * Invisible Insight Component
 * Displays AI-generated insights that feel like inner wisdom
 * When dismissed from floating view, insight remains in notification center
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStrategicInsight, useInvisibleInsights } from '../hooks/useInvisibleInsights';
import { useInsightEngagement } from '../hooks/useInsightEngagement';
import { Lightbulb, Sparkles, X } from 'lucide-react';
import analytics from '@/shared/utils/analytics/analytics';

/**
 * Floating insight that appears subtly
 */
export function FloatingInsight({ onClose }) {
  const navigate = useNavigate();
  const { insight, showInsight, hideFloating } = useStrategicInsight();
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  // Track engagement
  const insightContent = insight?.insights?.[0];
  const { trackAction } = useInsightEngagement(
    insight?.id,
    {
      type: insightContent?.type || 'floating_insight',
      content: insightContent?.content || ''
    }
  );

  useEffect(() => {
    if (insight && !hasShown) {
      // If the user recently saved an entry, delay 120s+ to avoid interrupting their session.
      // Otherwise use a 10s base delay so insights appear naturally.
      // Guarded: WKWebView with ITP can throw on sessionStorage access.
      let savedAtRaw = '0';
      try { savedAtRaw = sessionStorage.getItem('clarity_last_entry_saved') || '0'; } catch { /* ignore */ }
      const savedAt = parseInt(savedAtRaw, 10);
      const msSinceSave = Date.now() - savedAt;
      const recentlySaved = savedAt > 0 && msSinceSave < 120000; // within 2 minutes
      const baseDelay = recentlySaved
        ? Math.max(0, 120000 - msSinceSave)  // wait out the 2-minute window
        : 10000;                               // 10s base on other pages
      const delay = baseDelay + Math.random() * 20000; // + up to 20s random jitter

      const timer = setTimeout(() => {
        setIsVisible(true);
        // NOTE: do NOT call showInsight() here — marking displayedAt immediately
        // causes the Firestore snapshot to update, which clears currentInsight and
        // auto-dismisses the card before the user can read it. We mark it only on
        // explicit user interaction (dismiss or explore).
        setHasShown(true);
        analytics.trackEvent('insight_viewed', {
          insight_type: insightContent?.type || 'floating',
          source: 'floating_card',
        });
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [insight, hasShown]);

  const handleDismiss = async () => {
    setIsVisible(false);
    analytics.trackEvent('insight_dismissed', {
      insight_type: insightContent?.type || 'floating',
      source: 'floating_card',
    });
    // Mark as displayed so it moves from floating → notification bell history.
    // Do NOT permanently dismiss — insight stays in the bell for later reading.
    await showInsight();
    await hideFloating();
    if (onClose) onClose();
  };

  const handleExplore = async () => {
    setIsVisible(false);
    analytics.trackEvent('insight_explored', {
      insight_type: insightContent?.type || 'floating',
      source: 'floating_card',
    });
    await trackAction('explore_insights');
    await showInsight();
    await hideFloating();
    if (onClose) onClose();
    navigate('/insights');
  };

  if (!insight || !isVisible) return null;

  return (
    <div
      className="fixed left-4 right-4 max-w-sm mx-auto bg-[#1a1410] border border-white/15 rounded-2xl p-4 shadow-2xl clarity-reveal z-[45]"
      style={{ bottom: 'calc(60px + env(safe-area-inset-bottom, 8px) + 12px)' }}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-[#ff9e5e]" />
          <span className="clarity-label">
            A thought
          </span>
        </div>
        <button
          onClick={handleDismiss}
          className="min-w-[44px] min-h-[44px] flex items-center justify-center text-white/40 hover:text-white transition-colors -mr-2 -mt-2"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <p className="clarity-quote text-sm text-white/85 leading-relaxed">
        {insightContent?.content || ''}
      </p>

      {insight.quote && (
        <p className="clarity-quote text-xs text-white/40 mt-3 border-l border-white/10 pl-3">
          {insight.quote}
        </p>
      )}

      <div className="mt-4 flex justify-between items-center gap-2">
        <button
          onClick={handleDismiss}
          className="text-xs text-white/40 hover:text-white transition-colors font-light min-h-[44px] flex items-center justify-center px-2"
        >
          Not now
        </button>
        <button
          onClick={handleExplore}
          className="text-xs text-[#ff9e5e] hover:text-[#ff8e4e] transition-colors font-light min-h-[44px] flex items-center justify-center px-2"
        >
          Explore insights →
        </button>
      </div>
    </div>
  );
}

/**
 * Inline insight display for entry view
 */
export function EntryInsight({ entryId }) {
  const { insights, dismissInsight } = useInvisibleInsights();
  const [dismissed, setDismissed] = useState(false);

  const entryInsight = insights.find((i) => i.entryId === entryId && !i.dismissed);

  // Track engagement
  const insightContent = entryInsight?.insights?.[0];
  const { trackDismissed } = useInsightEngagement(
    entryInsight?.id,
    {
      type: insightContent?.type || 'entry_insight',
      content: insightContent?.content || ''
    }
  );

  if (!entryInsight || dismissed) return null;

  const handleDismiss = async () => {
    setDismissed(true);
    // Track dismissal
    await trackDismissed();
    await dismissInsight(entryInsight.id);
  };

  return (
    <div className="border-l-2 border-[#ffb85e]/30 pl-4 py-1 my-4 clarity-reveal-1">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#ff9e5e]" />
          <span className="clarity-label text-[#ff9e5e]/70">
            From your writing
          </span>
        </div>
        <button
          onClick={handleDismiss}
          className="text-white/40 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-2">
        {entryInsight.insights.map((insight) => (
          <p
            key={insight.content}
            className="clarity-quote text-sm text-white/85 leading-relaxed"
          >
            {insight.content}
          </p>
        ))}
        {entryInsight.quote && (
          <p className="clarity-quote text-xs text-white/40 mt-2 border-l border-[#ffb85e]/20 pl-3">
            {entryInsight.quote}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Subtle notification badge showing undisplayed insights
 */
export function InsightBadge() {
  const { hasUndisplayed } = useInvisibleInsights();

  if (!hasUndisplayed) return null;

  return (
    <div className="absolute -top-1 -right-1 w-2 h-2 bg-[#ff9e5e] rounded-full animate-pulse" />
  );
}

// CSS is now in main CSS file to avoid CSP issues

export default FloatingInsight;
